import React, { useState, useMemo, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress,
    Box,
    TablePagination,
    Typography,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import en_ja from '../../translations/en_ja';

const CustomTable = ({ columns = [], rows = [], loading = false, searchTerm = '', showIndexColumn = false, resetTrigger }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnId) => {
        if (sortConfig.key !== columnId) return null;
        return sortConfig.direction === 'asc' ? (
            <KeyboardArrowUpIcon fontSize="small" />
        ) : (
            <KeyboardArrowDownIcon fontSize="small" />
        );
    };

    const filteredRows = useMemo(() => {
        if (!searchTerm) return rows;
        const term = searchTerm.toLowerCase();
        return rows.filter((row) =>
            columns.some((col) => {
                const value = col.accessor ? col.accessor(row) : row[col.id];
                return value?.toString().toLowerCase().includes(term);
            })
        );
    }, [rows, searchTerm, columns]);

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortConfig.direction === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }
            return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [filteredRows, sortConfig]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        setPage(0);
    }, [searchTerm, resetTrigger]);

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "16px",
                border: "1px solid #e9ecef",
                backgroundColor: "white",
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                    transform: "translateY(-4px)",
                },
            }}
        >
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead
                        sx={{
                            backgroundColor: '#f8f9fa',
                            '& th': {
                                backgroundColor: '#f8f9fa',
                                py: 2,
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#495057',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                borderBottom: '1px solid #e9ecef',
                            },
                            "&:hover": { backgroundColor: "#f1f3f4" }
                        }}
                    >
                        <TableRow>
                            {showIndexColumn && <TableCell>#</TableCell>}
                            {columns?.map((column) => (
                                <TableCell
                                    key={column.id}
                                    onClick={() => column.sortable && handleSort(column.id)}
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "#495057",
                                        py: 2,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        cursor: column.sortable ? 'pointer' : 'default',
                                        userSelect: 'none',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: column.maxWidth || '200px',
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {en_ja[column.label] || column.label}
                                        {column.sortable && getSortIcon(column.id)}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} sx={{ p: 0 }}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && paginatedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            py: 1,
                                            color: "text.secondary",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                            {en_ja["No data found"]}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows.map((row, index) => (
                                <TableRow key={row.id || index} hover sx={{ '& td': { py: 2 } }}>
                                    {showIndexColumn && (
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    )}
                                    {columns.map((column) => (
                                        <TableCell key={column.id}>
                                            {column.render ? column.render(row) : row[column.id]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    px: 3,
                    borderTop: "1px solid #e9ecef",
                    background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
                }}
            >

                {/* <Typography
                    variant="body2"
                    color="#6c757d"
                    sx={{ fontSize: "14px", fontWeight: "500" }}
                >
                    {en_ja["Showing"]} {Math.min(page * rowsPerPage + 1, filteredRows.length)}-
                    {Math.min((page + 1) * rowsPerPage, filteredRows.length)} {en_ja["of"]}{" "}
                    {filteredRows.length}
                </Typography> */}

                <TablePagination
                    component="div"
                    count={filteredRows?.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage={en_ja["Rows per page:"]}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${en_ja["Showing"]} ${from}-${to} ${en_ja["of"]} ${count}`
                    }
                    sx={{
                        "& .MuiTablePagination-displayedRows": {
                            fontSize: "14px",
                            fontWeight: "500",
                        },
                        "& .MuiTablePagination-selectLabel": {
                            fontSize: "14px",
                            fontWeight: "500",
                        },
                        "& .MuiTablePagination-select": {
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            "&:hover": { backgroundColor: "rgba(220, 53, 69, 0.04)" },
                        },
                        "& .MuiTablePagination-actions": {
                            "& .MuiIconButton-root": {
                                borderRadius: "8px",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    backgroundColor: "rgba(220, 53, 69, 0.04)",
                                    color: "#dc3545",
                                    transform: "scale(1.1)",
                                },
                            },
                        },
                    }}
                />
            </Box>
        </Paper>
    );
};

export default CustomTable;
